import React, { useCallback } from "react";
import {
  Box,
  Button,
  Divider,
  LinearProgress,
  Typography,
  linearProgressClasses,
} from "@mui/material";
import NFItem from "./NFItem";
import styled from "@emotion/styled";
import { useDispatch, useSelector } from "react-redux";
import {
  add,
  changeStatus,
  addErrors,
  uploadFile,
  setIsFilesUploaded,
  resetTotalUploaded,
  setInvoicesUploadSuccessfully,
  reset,
} from "../../../../store/uploadingFileSlice";
import { useDropzone } from "react-dropzone";
import ReceivableService from "../../../../services/ReceivableService";

const BorderLinearProgress = styled(LinearProgress)(({ theme }) => ({
  height: 10,
  borderRadius: 5,
  [`&.${linearProgressClasses.colorPrimary}`]: {
    backgroundColor:
      theme.palette.grey[theme.palette.mode === "light" ? 200 : 800],
  },
}));

const LinearBuffer = ({ progress, totalSize }) => {
  return (
    <Box sx={{ width: "100%", padding: "8px 0px" }}>
      <Box display={"inline-flex"} width={"100%"} paddingBottom={"8px"}>
        <Box>
          <Typography
            sx={{
              fontWeight: 400,
              fontSize: 12,
            }}
            color="#575757"
            noWrap
          >
            {(totalSize / 1024).toFixed(2)} KB
          </Typography>
        </Box>
        <Box flexGrow={1} />

        <Box>
          <Typography
            sx={{
              fontWeight: 400,
              fontSize: 12,
            }}
            color="#575757"
            noWrap
          >
            {progress?.toFixed(0)}%
          </Typography>
        </Box>
      </Box>

      <BorderLinearProgress variant="determinate" value={progress} />
    </Box>
  );
};

const NFBoard = ({ files, handleDrawerClose }) => {
  const dispatch = useDispatch();

  const onDrop = useCallback(
    (acceptedFiles) => {
      dispatch(resetTotalUploaded());
      dispatch(add(acceptedFiles));
    },
    [dispatch]
  );

  const { open } = useDropzone({
    onDrop,
    accept: { "text/*": [".xml"] },
  });

  const uploadFiles = async () => {
    dispatch(setIsFilesUploaded());
    try {
      await Promise.all(
        files.map(async (item) => {
          try {
            dispatch(changeStatus({ id: item.id, status: "inProgress" }));

            const response = await ReceivableService.uploadFile(item.file);

            dispatch(changeStatus({ id: item.id, status: "completed" }));
            if (response.status === 200) {
              dispatch(setInvoicesUploadSuccessfully());
            }
          } catch (error) {
            dispatch(
              addErrors({ id: item.id, message: error.response.data.message })
            );
          } finally {
            const uploadFileUploading = (value) => {
              dispatch(uploadFile(value));
            };

            uploadFileUploading(item.file.size);
            dispatch(changeStatus({ id: item.id, status: "completed" }));
          }
        })
      );
    } catch (error) {
      console.log(error);
    }
  };

  const { totalUploaded, totalSize, invoicesUploadSuccessfully } = useSelector(
    (state) => state.uploading
  );

  const isUploadingCompleted = () => totalUploaded === totalSize;

  let percent = Math.floor((totalUploaded * 100) / totalSize);

  return (
    <Box>
      <Box maxWidth={true} display={"flex"} justifyContent={"end"}>
        {!totalUploaded && (
          <Button
            onClick={open}
            variant="outlined"
            color="secondary"
            sx={{
              px: "16px",
              py: "10px",
            }}
          >
            <Typography
              sx={{
                fontWeight: 500,
                fontSize: 14,
              }}
              color="inherit"
              noWrap
            >
              Adicionar notas
            </Typography>
          </Button>
        )}
      </Box>
      <Box
        mt={"16px"}
        sx={{
          borderWidth: 2,
          borderRadius: 2,
          borderColor: "#D9D9D9",
          borderStyle: "dashed",
          backgroundColor: "#fafafa",
          color: "#949292",
          outline: "none",
          transition: "border .24s ease-in-out",
          minHeight: "412px",
        }}
      >
        <Box maxWidth={true} display={"flex"} justifyContent={"space-between"}>
          <Box display={"flex"} padding={"16px 0px 16px 16px"}>
            <Typography fontWeight="400" fontSize="14px" color={"#575757"}>
              Nome do arquivo
            </Typography>
          </Box>
          <Box
            sx={{
              flexGrow: 1,
            }}
          />
          <Divider orientation="vertical" flexItem />
          <Box
            minWidth={"156px"}
            display={"flex"}
            padding={2}
            justifyContent={"start"}
            alignItems={"center"}
          >
            <Typography fontWeight="400" fontSize="14px" color={"#575757"}>
              Status
            </Typography>
          </Box>
        </Box>
        <Divider />

        <Box>
          {files.map((file, index) => {
            if (index !== files.length - 1) {
              return (
                <Box key={file.id}>
                  <NFItem item={file} />
                  <Divider />
                </Box>
              );
            }

            return <NFItem key={file.id} item={file} />;
          })}
        </Box>
      </Box>

      <LinearBuffer progress={percent} totalSize={totalSize} />

      <Box
        maxWidth={true}
        display={"flex"}
        justifyContent={"end"}
        paddingTop={2}
      >
        {!totalUploaded && (
          <Button
            onClick={uploadFiles}
            disabled={isUploadingCompleted()}
            variant="contained"
            color="secondary"
            sx={{
              px: "16px",
              py: "10px",
            }}
          >
            <Typography
              sx={{
                fontWeight: 500,
                fontSize: 14,
              }}
              color="inherit"
              noWrap
            >
              Carregar
            </Typography>
          </Button>
        )}
        {isUploadingCompleted && invoicesUploadSuccessfully && (
          <Button
            onClick={() => {
              handleDrawerClose(invoicesUploadSuccessfully);
              dispatch(reset());
            }}
            variant="contained"
            color="secondary"
            sx={{
              px: "16px",
              py: "10px",
            }}
          >
            <Typography
              sx={{
                fontWeight: 500,
                fontSize: 14,
              }}
              color="inherit"
              noWrap
            >
              Visualizar Recebíveis
            </Typography>
          </Button>
        )}
        {totalUploaded > 0 && !invoicesUploadSuccessfully && (
          <Box>
            <Button
              onClick={() => {
                handleDrawerClose(false);
                dispatch(reset());
              }}
              variant="outlined"
              color="secondary"
              sx={{
                px: "16px",
                py: "10px",
                mr: "12px",
              }}
            >
              <Typography
                sx={{
                  fontWeight: 500,
                  fontSize: 14,
                }}
                color="inherit"
                noWrap
              >
                Finalizar
              </Typography>
            </Button>
            <Button
              onClick={() => {
                dispatch(reset());
              }}
              variant="contained"
              color="secondary"
              sx={{
                px: "16px",
                py: "10px",
              }}
            >
              <Typography
                sx={{
                  fontWeight: 500,
                  fontSize: 14,
                }}
                color="inherit"
                noWrap
              >
                Adicionar mais notas
              </Typography>
            </Button>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default NFBoard;
